"use client";

import { useEffect, useRef, ReactNode, useCallback } from "react";

import useWithAnalytics from "@stadium/services/analytics/hooks/use-with-analytics";
import { ANALYTICS_EVENT_ACTION_VIEWED } from "@stadium/services/analytics/constants/default";

import { ANALYTICS_TARGET_INFO_SECTION } from "./constant";

type EventLocationAnalytics = {
  eventProperties?: Record<string, unknown>;
};

type SectionViewTrackerProps = {
  eventLocationAnalytics?: EventLocationAnalytics;
  data?: { intent?: string };
  children: ReactNode;
  [key: string]: unknown;
};

const SectionViewTracker = ({
  eventLocationAnalytics,
  data,
  children,
}: SectionViewTrackerProps) => {
  const observerRef = useRef<IntersectionObserver | null>(null);
  const hasTracked = useRef(false);
  const withAnalytics = useWithAnalytics();

  const analyticsConfigRef = useRef({
    ...eventLocationAnalytics,
    track: true,
    target: ANALYTICS_TARGET_INFO_SECTION,
    action: ANALYTICS_EVENT_ACTION_VIEWED,
    eventProperties: {
      ...eventLocationAnalytics?.eventProperties,
      section_type: data?.intent,
    },
  });

  // Keep ref in sync with latest props without triggering effect churn
  useEffect(() => {
    analyticsConfigRef.current = {
      ...eventLocationAnalytics,
      track: true,
      target: ANALYTICS_TARGET_INFO_SECTION,
      action: ANALYTICS_EVENT_ACTION_VIEWED,
      eventProperties: {
        ...eventLocationAnalytics?.eventProperties,
        section_type: data?.intent,
      },
    };
  }, [eventLocationAnalytics, data?.intent]);

  const setRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }

      if (node && !hasTracked.current) {
        const observer = new IntersectionObserver(
          ([entry]) => {
            if (entry.isIntersecting && !hasTracked.current) {
              hasTracked.current = true;
              withAnalytics(undefined, analyticsConfigRef.current)();
              observer.disconnect();
            }
          },
          { threshold: 0.3 },
        );

        observer.observe(node);
        observerRef.current = observer;
      }
    },
    [withAnalytics],
  );

  useEffect(() => {
    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  return <div ref={setRef}>{children}</div>;
};

export default SectionViewTracker;
